
import VueApp from '@/@types/app/VueApp';
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import EmptyStateCard from '@/components/layout/cards/EmptyStateCard.vue';
import TipTap from '@/components/tiptap/TipTap.vue';
import TipTapCommentsIconsSidebar from '@/components/TiptapCommentsIconsSidebar/TipTapCommentsIconsSidebar.vue';
import { SEARCH_TEXT_HIGHLIGHT_COLOR } from '@/constants/SharedConsts';
import helper from '@/helper';
import { Article } from '@/models/article/Article';
import { TextTab } from '@/models/article/TextTab';
import * as routerNames from '@/routerNames';
import articleEditService from '@/services/article/ArticleEditService';
import mobileResponsiveService from '@/services/MobileResponsiveService';
import { workspaceService } from '@/services/workspace/WorkspaceService';
import { commitSetArticleEditorTextTabs } from '@/store/commits/articleEditorCommits';
import { commitSetArticleMode, commitSetAutoPlay } from '@/store/commits/sharedCommits';
import { Mutations } from '@/store/mutations';
import { ElLoadingComponent } from 'element-ui/types/loading';
import { Component, Prop, Watch } from 'vue-property-decorator';
import apiService from '@/services/ApiService';
import logger from '@/other/Logger';

const SCROLL_TO_TEXT_DELAY = 100;
const CLEAR_ARTICLE_TEXT_PROPS_DELAY = 2000;

@Component({
  components: {
    TipTapCommentsIconsSidebar,
    TipTap,
    EmptyStateCard,
    DiffBanner: () => import('@/components/tiptap/DiffBanner/DiffBanner.vue'),
    ReconnectDialog: () => import('@/components/connectors/ReconnectDialog.vue'),
  },
})
export default class ArticleTextEditor extends VueApp {
  @Prop({ default: true }) isEditMode: boolean;
  @Prop(Article) article: Article;
  @Prop(Boolean) isSharedMode: boolean;

  loader: ElLoadingComponent = null;
  originalText: null | string = null;
  showReconnectDialog = false;

  get documentId() {
    if (this.selectedTextTab?.id) {
      return `${this.articleId}:${this.selectedTextTab.id}`;
    } else {
      return this.articleId;
    }
  }

  get isArticleEditPage() {
    return [
      routerNames.ARTICLE_CREATE,
      routerNames.ARTICLE_EDIT,
      routerNames.ARTICLE_VERSION_EDIT,
      routerNames.DRAFT_EDIT,
    ].includes(this.$route.name);
  }

  get isDraftEditPage() {
    return this.$route.name === routerNames.DRAFT_EDIT;
  }

  get isEditPage() {
    return this.isDraftEditPage || this.isArticleEditPage;
  }

  get selectedTextTab() {
    const souceOfActiveTab = this.isEditPage
      ? this.$store.state.articleEditor.activeTextTabId
      : this.$store.state.workspaceView.activeTextTabId;
    const activeTextTabId = souceOfActiveTab;

    if (!activeTextTabId || !this.article || !this.article.textTabs) {
      return null;
    }

    const selectedTab = this.article.textTabs.find((item) => item.id === activeTextTabId);

    return selectedTab || null;
  }

  get articleId() {
    return this.article.original ? this.article.original : this.article.id;
  }

  get showEmptyStateCard() {
    return !this.article?.textTabs?.length;
  }

  get isDiffModeActive(): boolean {
    return this.$store.state.diffMode?.active || false;
  }

  get editorKey() {
    // Include the article id alongside the tab id so navigating between two articles that share
    // a tab id (e.g. the 'default' tab on every connector-synced article) forces TipTap to remount
    // and read the new content. Without the article id, Vue keeps the old editor instance and
    // shows the previous article's body.
    const articleKey = this.article.draft ? this.article.original : this.article.id;
    const tabKey = this.selectedTextTab?.id || 'no-tab';
    const base = `${articleKey}:${tabKey}`;
    return this.isDiffModeActive ? `${base}-diff` : base;
  }

  get isMobile() {
    return mobileResponsiveService.isMobileView;
  }

  get articleContent() {
    if (this.isDiffModeActive && this.selectedTextTab) {
      const diffTab = this.$store.state.diffMode.tabs.find(
        (t: { name: string }) => t.name === this.selectedTextTab.title
      );
      if (diffTab) return diffTab.html;
    }
    return this.originalText ? this.originalText : this.selectedTextTab ? this.selectedTextTab.text : null;
  }

  get articleTextPropsToFind() {
    return this.$store.state.articleTextPropsToFind;
  }

  get userCanManageArticles() {
    return workspaceService.workspaceView.workspace.canEditArticles();
  }

  get isShowFooter() {
    return this.$store.state.articleVersionView.isShowFooter;
  }

  get isExternalArticle(): boolean {
    return !!this.article && this.article.isExternalSource();
  }

  get sourceName(): string {
    return this.article?.sourceMetadata?.sourceName || this.article?.sourceType || '';
  }

  get creatorLabel(): string {
    const user = this.article?.user;
    if (user && typeof user === 'object' && user.firstName) {
      return `Added by ${user.firstName}${user.lastName ? ' ' + user.lastName : ''}`;
    }
    return '';
  }

  get isStaleArticle(): boolean {
    return this.article?.syncStatus === 'STALE';
  }

  get syncStatusLabel(): string {
    const status = this.article?.syncStatus;
    if (status === 'SYNCED') return 'Synced';
    if (status === 'ERROR') return 'Sync Error';
    if (status === 'SYNCING') return 'Syncing';
    if (status === 'STALE') return 'Out of date';
    return status || '';
  }

  get syncStatusClass(): string {
    const status = this.article?.syncStatus;
    if (status === 'SYNCED') return 'external-sync-bar__status--synced';
    if (status === 'ERROR') return 'external-sync-bar__status--error';
    if (status === 'SYNCING') return 'external-sync-bar__status--syncing';
    if (status === 'STALE') return 'external-sync-bar__status--stale';
    return '';
  }

  get lastSyncedLabel(): string {
    if (!this.article?.lastSyncedAt) return '';
    const date = new Date(this.article.lastSyncedAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  get showNoTabSelected() {
    return !this.selectedTextTab && this.article.hasText() && this.$store.state.articleMode === ArticleEditMode.NOTES;
  }

  get workspaceId(): string {
    const ws = this.article?.workspace;
    if (typeof ws === 'object' && ws !== null) return (ws as any).id || '';
    return (ws as string) || '';
  }

  handleReconnect() {
    this.showReconnectDialog = true;
  }

  async handleReconnectSuccess(result: Record<string, unknown>) {
    this.showReconnectDialog = false;
    if (result?.workspaceOrder) {
      this.$store.commit('setWorkspaceOrder', result.workspaceOrder);
    }
    await workspaceService.loadArticlesItems();
    if (this.article?.id) {
      const { dispatchOpenSelectedArticle } = await import('@/store/dispatchers/workspaceViewDispatchers');
      await dispatchOpenSelectedArticle({ articleId: this.article.id });
    }
  }

  handleEditArticle() {
    this.$router.push(`/article/${this.article.id}/edit/${ArticleEditMode.NOTES}`);
  }

  async handleCreateNotes() {
    const textTab = new TextTab();
    this.loader = this.$loading({
      lock: true,
      text: 'Creating text tab',
    });

    const currentTabs = this.$store.state.articleEditor.article.textTabs;
    let newTextTabs = Array.isArray(currentTabs) ? [...currentTabs, textTab] : [textTab];

    try {
      commitSetArticleEditorTextTabs(newTextTabs);
      await articleEditService.saveAsDraft();

      const newTextTab = this.article.textTabs[this.article.textTabs.length - 1];
      articleEditService.setActiveTextTab({ article: this.article, textTabId: newTextTab.id });
      commitSetArticleMode(ArticleEditMode.NOTES);
    } finally {
      this.loader.close();
      this.loader = null;
    }
  }

  async updateArticleText(text: string) {
    // If selected text tab in editor, update this tab text, if not, update default notes tab
    if (this.selectedTextTab) {
      const currentTabs = this.$store.state.articleEditor.article.textTabs;
      const tab = currentTabs.find((item: TextTab) => item.id === this.selectedTextTab.id);

      if (tab) tab.text = text;

      await articleEditService.saveAsDraft();
    }
  }

  created() {
    if (this.article) {
      this.setArticle();
      this.searchAndHighlightText();
      this.loadTranscription();
    }
    if (this.isShowFooter) {
      this.addBottomSpace();
    }
  }

  searchAndHighlightText() {
    if (this.articleTextPropsToFind.length) {
      this.originalText = null;
      let firstItemStartIndex = -1;
      let lastItemEndIndex = -1;
      let findTextTabId: string | null = null;

      for (const { startIndex, textToFind, queryLength, textTabId } of this.articleTextPropsToFind) {
        if (!textToFind) return;
        if (firstItemStartIndex === -1) {
          firstItemStartIndex = startIndex;
        }
        lastItemEndIndex = startIndex + queryLength;
        findTextTabId = textTabId;
      }

      if (firstItemStartIndex !== -1 && lastItemEndIndex !== -1) {
        const searchTextToHighlight = this.articleTextPropsToFind
          .map(({ textToFind }: { textToFind: string }) => textToFind)
          .join(' ');

        const mark = `<mark data-id="tmp-search-highlight" data-color='${SEARCH_TEXT_HIGHLIGHT_COLOR}'>${searchTextToHighlight}</mark>`;
        const replacement = !this.originalText ? mark : mark + ' ';
        const firstTextTab = this.article.textTabs[0];

        const articleText = !this.originalText
          ? this.selectedTextTab
            ? this.selectedTextTab.text
            : firstTextTab.text
          : null;

        if (!findTextTabId || this.selectedTextTab?.id !== findTextTabId) return;

        this.originalText = helper.replaceTextFromTo(
          articleText,
          replacement,
          firstItemStartIndex,
          lastItemEndIndex - firstItemStartIndex
        );
      }

      window.setTimeout(() => {
        this.scrollToHighlightedText();
      }, SCROLL_TO_TEXT_DELAY);

      window.setTimeout(() => {
        commitSetAutoPlay(null);
        this.$store.commit(Mutations.SET_ARTICLE_TEXT_PROPS_TO_FIND, []);
      }, CLEAR_ARTICLE_TEXT_PROPS_DELAY);
    }
  }

  scrollToHighlightedText() {
    const isArticleEditMode = this.$route.name === 'DraftEdit';
    const container = document.querySelector<HTMLElement>(
      isArticleEditMode ? '.el-tiptap-editor__content' : '.workspace-container'
    );
    if (container) container.scrollTo(0, 0); // reset scroll position

    const foundElement = container.querySelector<HTMLElement>(`mark[data-color="${SEARCH_TEXT_HIGHLIGHT_COLOR}"]`);
    const markedTextOffset = container.clientHeight / 2;
    const containerRect = container?.getBoundingClientRect();
    const foundElementRect = foundElement?.getBoundingClientRect();

    if (!foundElementRect || !containerRect) return;

    const foundElementOffset = foundElementRect.top - containerRect.top - markedTextOffset;

    if (foundElement && container) {
      container.scrollTo(0, foundElementOffset);
    } else {
      //text wrapped in code block can not be highlighted with mark. in this case we need just scroll
      const preContainer = container.querySelectorAll<HTMLElement>('.el-tiptap-editor__content pre');
      if (preContainer && container) {
        preContainer.forEach((element: HTMLElement) => {
          const { textToFind } = this.articleTextPropsToFind[0];
          if (element.textContent.includes(textToFind)) {
            container.scrollTo(0, element.offsetTop);
          }
        });
      }
    }
  }

  addBottomSpace() {
    setTimeout(() => {
      const tiptapContent: HTMLElement = document.querySelector('.el-tiptap-editor__content');
      if (tiptapContent) {
        if (this.isShowFooter) {
          tiptapContent.style.paddingBottom = '150px';
        } else {
          tiptapContent.style.paddingBottom = '85px';
        }
      }
    }, 1);
  }

  async loadTranscription() {
    const article = articleEditService.articleEditor.article;

    if (!this.isArticleEditPage || !article) return;

    this.$store.commit('transcriptionSidebarSetTranscription', []);

    try {
      const resSubtitles = await apiService.article.getTranscription(article.id);
      const subtitles = resSubtitles.data.transcription;

      if (subtitles.length) {
        const parsedSubtitles = JSON.parse(subtitles);
        this.$store.commit('transcriptionSidebarSetTranscription', parsedSubtitles);
      } else {
        this.$store.commit('transcriptionSidebarSetTranscription', []);
      }
    } catch (err) {
      logger.error('Failed to load transcription for the article');
      this.$store.commit('transcriptionSidebarSetTranscription', []);
    }
  }

  @Watch('article')
  @Watch('selectedTextTab')
  onArticleOrTabChange() {
    this.originalText = null;
    this.setArticle();
    this.searchAndHighlightText();
  }

  @Watch('articleTextPropsToFind')
  search() {
    this.searchAndHighlightText();
  }

  @Watch('isShowFooter')
  changePadding() {
    this.addBottomSpace();
  }

  @Watch('isDiffModeActive')
  onDiffModeChanged(isActive: boolean) {
    if (isActive) {
      this.$nextTick(() => {
        setTimeout(() => {
          this.scrollToFirstChange();
        }, 150);
      });
    }
  }

  scrollToFirstChange() {
    const container = document.querySelector('.el-tiptap-editor__content');
    if (!container) return;
    const firstChange = container.querySelector('ins, del');
    if (firstChange) {
      firstChange.classList.add('diff-highlight-pulse');
      firstChange.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        firstChange.classList.remove('diff-highlight-pulse');
      }, 2000);
    }
  }

  setArticle() {
    this.$store.commit('setArticleEditorArticle', this.article);
  }
}
